/**
 * Per-label budget statistics for the renovation tab.
 *
 * Three bars per renovation label:
 *  1. Strategic budget reserved — sum of investment-line estimatedCost (per
 *     line.label, only counting non-released lines since released amounts
 *     went back to the bucket).
 *  2. Budget taken in project parts — sum of part.investmentAssignments
 *     amounts grouped by part.label.
 *  3. Invoiced — sum of part.documentLines totalInclVat (type INVOICE)
 *     grouped by part.label.
 *
 * Self-fetches data from the same endpoints the rest of the renovation tab
 * already uses, then aggregates client-side. Filter chips let the user pick
 * which labels to compare.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  CaretDown,
  CaretUp,
  ChartBar,
  Funnel,
  Tag,
} from "@phosphor-icons/react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip as ChartTooltip,
} from "chart.js";

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, Legend);

interface LabelBudgetStatisticsSectionProps {
  buildingId: string;
}

interface LabelBucket {
  labelId: number | "no-label";
  labelName: string;
  labelType?: string;
  reserved: number;
  partsAllocated: number;
  invoiced: number;
}

const NO_LABEL_KEY: "no-label" = "no-label";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

// Brand-aligned bar colours (mirrors helpdesk/acquisitions chartConfig).
const BAR_COLOURS = {
  reserved: "#5B7304", // moss-primary — strategisch reserved
  partsAllocated: "#EA8162", // brick-primary — toegewezen aan projectonderdelen
  invoiced: "#3B82F6", // blue — gefactureerd
};

export const LabelBudgetStatisticsSection = ({
  buildingId,
}: LabelBudgetStatisticsSectionProps) => {
  const { session } = useSession();

  const [items, setItems] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // Excluded label ids (or NO_LABEL_KEY). Empty = show everything.
  const [excludedLabels, setExcludedLabels] = useState<Set<number | "no-label">>(
    new Set(),
  );

  const fetchAll = useCallback(async () => {
    if (!session || !buildingId) return;
    setIsLoading(true);
    try {
      const [itemsRes, projectsRes] = await Promise.all([
        fetchWithAuth(
          `${API_URL}/investment/building/${buildingId}/with-assignments`,
          { method: "GET" },
          session,
        ),
        fetchWithAuth(
          `${API_URL}/renovation/project/building/${buildingId}`,
          { method: "GET" },
          session,
        ),
      ]);
      if (itemsRes.ok) {
        const data = await itemsRes.json();
        setItems(Array.isArray(data?.items) ? data.items : []);
      }
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("[LabelBudgetStatisticsSection] fetch failed", err);
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Refresh on the same cross-component event budget buckets use, so when a
  // line is released / a project part edited the chart stays in sync.
  useEffect(() => {
    const handler = () => fetchAll();
    window.addEventListener("renovation-project-refresh", handler);
    window.addEventListener("budget-buckets-changed", handler);
    return () => {
      window.removeEventListener("renovation-project-refresh", handler);
      window.removeEventListener("budget-buckets-changed", handler);
    };
  }, [fetchAll]);

  // ------- Aggregate per label -------
  const buckets = useMemo<LabelBucket[]>(() => {
    const map = new Map<number | "no-label", LabelBucket>();

    const ensure = (
      labelId: number | "no-label" | null | undefined,
      labelName?: string,
      labelType?: string,
    ): LabelBucket => {
      const key: number | "no-label" =
        typeof labelId === "number" ? labelId : NO_LABEL_KEY;
      let bucket = map.get(key);
      if (!bucket) {
        bucket = {
          labelId: key,
          labelName:
            key === NO_LABEL_KEY ? "Geen label" : labelName || `Label #${key}`,
          labelType,
          reserved: 0,
          partsAllocated: 0,
          invoiced: 0,
        };
        map.set(key, bucket);
      } else if (key !== NO_LABEL_KEY && labelName && bucket.labelName.startsWith("Label #")) {
        // Late-binding: improve the displayed name once we see it.
        bucket.labelName = labelName;
        bucket.labelType = labelType ?? bucket.labelType;
      }
      return bucket;
    };

    // Two distinct aggregations — they are NOT the same number:
    //
    //   reserved (Strategisch gereserveerd)
    //     ↳ grouped by INVESTMENT LINE label
    //     ↳ sum of each line's strategic claim on its bucket
    //         min(estimatedCostExclVat, bucket.totalAmount)  for active lines
    //         min(est, cap) − releasedAmount                 for released lines
    //     This is "what a category has booked from the strategic budgets,
    //     even if no project part is attached yet".
    //
    //   partsAllocated (Toegewezen op projectonderdelen) + invoiced
    //     ↳ grouped by PROJECT PART label
    //     ↳ sum of part.investmentAssignments.assignedAmount
    //     ↳ invoiced = sum of part.documentLines (INVOICE)
    //     This is "what is actually committed to / invoiced for project work
    //     under that category".
    //
    // The two often differ — the line label might not match the part label
    // (a Badkamer line can fund an Afbraakwerken part), and unattached lines
    // contribute to `reserved` but not to `partsAllocated`. Earlier this
    // chart computed both from per-assignment fields grouped by part.label,
    // which made them collapse to the same value whenever no assignment
    // overflowed its line cap.

    // Pass 1 — investment lines: reserved per line.label.
    for (const item of items) {
      const labelId = item?.label?.id ?? null;
      const bucket = ensure(labelId, item?.label?.name, item?.label?.type);

      const est = Number(item?.estimatedCostExclVat) || 0;
      const cap = Number(item?.bucket?.totalAmount) || est;
      const strategic = item?.bucket?.id ? Math.min(est, cap || est) : 0;
      const released = item?.isReleased ? Number(item?.releasedAmount) || 0 : 0;
      bucket.reserved += Math.max(0, strategic - released);
    }

    // Pass 2 — project parts: partsAllocated + invoiced per part.label.
    for (const project of projects) {
      for (const part of project?.parts ?? []) {
        const labelId = part?.label?.id ?? null;
        const bucket = ensure(labelId, part?.label?.name, part?.label?.type);

        const assignments =
          part?.investmentAssignments ?? part?.budgetAssignments ?? [];
        for (const a of assignments) {
          const assigned =
            Number((a as any)?.assignedAmount) ||
            Number((a as any)?.amount) ||
            0;
          if (assigned <= 0) continue;
          bucket.partsAllocated += assigned;
        }

        // invoiced = sum of INVOICE document lines on this part
        for (const line of part?.documentLines ?? []) {
          const type = line?.document?.type;
          if (type === "Factuur" || type === "INVOICE") {
            const amt =
              Number(line?.totalInclVat) || Number(line?.totalExclVat) || 0;
            bucket.invoiced += amt;
          }
        }
      }
    }

    // Drop fully-empty buckets (label that has no money at all anywhere).
    return Array.from(map.values())
      .filter(
        (b) => b.reserved > 0 || b.partsAllocated > 0 || b.invoiced > 0,
      )
      .sort((a, b) => {
        const totalA = a.reserved + a.partsAllocated + a.invoiced;
        const totalB = b.reserved + b.partsAllocated + b.invoiced;
        return totalB - totalA;
      });
  }, [items, projects]);

  const toggleLabel = useCallback((id: number | "no-label") => {
    setExcludedLabels((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const includeAll = useCallback(() => setExcludedLabels(new Set()), []);
  const excludeAll = useCallback(
    () => setExcludedLabels(new Set(buckets.map((b) => b.labelId))),
    [buckets],
  );

  const visibleBuckets = useMemo(
    () => buckets.filter((b) => !excludedLabels.has(b.labelId)),
    [buckets, excludedLabels],
  );

  const totals = useMemo(() => {
    return visibleBuckets.reduce(
      (acc, b) => ({
        reserved: acc.reserved + b.reserved,
        partsAllocated: acc.partsAllocated + b.partsAllocated,
        invoiced: acc.invoiced + b.invoiced,
      }),
      { reserved: 0, partsAllocated: 0, invoiced: 0 },
    );
  }, [visibleBuckets]);

  const chartData = useMemo(
    () => ({
      labels: visibleBuckets.map((b) => b.labelName),
      datasets: [
        {
          label: "Strategisch gereserveerd",
          data: visibleBuckets.map((b) => Math.round(b.reserved)),
          backgroundColor: BAR_COLOURS.reserved,
          borderRadius: 4,
        },
        {
          label: "Toegewezen op projectonderdelen",
          data: visibleBuckets.map((b) => Math.round(b.partsAllocated)),
          backgroundColor: BAR_COLOURS.partsAllocated,
          borderRadius: 4,
        },
        {
          label: "Gefactureerd",
          data: visibleBuckets.map((b) => Math.round(b.invoiced)),
          backgroundColor: BAR_COLOURS.invoiced,
          borderRadius: 4,
        },
      ],
    }),
    [visibleBuckets],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { position: "top" as const, align: "end" as const },
        tooltip: {
          callbacks: {
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: (val: any) => formatCurrency(Number(val)),
            font: { size: 11 },
          },
          grid: { color: "rgba(0,0,0,0.05)" },
        },
      },
    }),
    [],
  );

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center gap-2 transition-opacity hover:opacity-80"
        >
          {isExpanded ? (
            <CaretUp weight="bold" size={16} className="text-neutral-500" />
          ) : (
            <CaretDown weight="bold" size={16} className="text-neutral-500" />
          )}
          <ChartBar weight="duotone" size={18} className="text-moss-primary" />
          <p className="text-lg font-semibold">Budgetstatistieken per label</p>
          <span className="text-neutral-secondary-mid text-sm">
            ({buckets.length} label{buckets.length === 1 ? "" : "s"})
          </span>
        </button>
      </header>

      {isExpanded && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          {isLoading ? (
            <div className="text-neutral-secondary-mid py-8 text-center text-sm">
              Statistieken berekenen...
            </div>
          ) : buckets.length === 0 ? (
            <div className="text-neutral-secondary-mid py-8 text-center text-sm">
              Nog geen budgetdata om te tonen.
            </div>
          ) : (
            <>
              {/* Filter chips: tap to (de)select labels */}
              <div className="mb-3 flex flex-wrap items-center gap-1.5">
                <Funnel
                  size={12}
                  weight="duotone"
                  className="text-neutral-secondary-mid"
                />
                <span className="text-neutral-secondary-mid text-[11px] font-semibold uppercase tracking-wide">
                  Labels
                </span>
                {buckets.map((b) => {
                  const active = !excludedLabels.has(b.labelId);
                  return (
                    <button
                      key={String(b.labelId)}
                      type="button"
                      onClick={() => toggleLabel(b.labelId)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                        active
                          ? "border-moss-primary/40 bg-moss-primary/10 text-moss-primary"
                          : "border-neutral-200 bg-neutral-50 text-neutral-400 line-through"
                      }`}
                      title={
                        active
                          ? "Klik om dit label te verbergen"
                          : "Klik om dit label te tonen"
                      }
                    >
                      <Tag size={10} weight="fill" />
                      {b.labelName}
                    </button>
                  );
                })}
                <span className="ml-auto flex gap-1">
                  <button
                    type="button"
                    onClick={includeAll}
                    className="text-moss-primary hover:underline text-[11px]"
                    disabled={excludedLabels.size === 0}
                  >
                    Toon alle
                  </button>
                  <span className="text-neutral-300 text-[11px]">·</span>
                  <button
                    type="button"
                    onClick={excludeAll}
                    className="text-neutral-secondary-mid hover:underline text-[11px]"
                    disabled={excludedLabels.size === buckets.length}
                  >
                    Verberg alle
                  </button>
                </span>
              </div>

              {/* KPI summary across visible labels */}
              <div className="mb-3 grid grid-cols-3 gap-2">
                <KpiTile
                  color={BAR_COLOURS.reserved}
                  label="Strategisch gereserveerd"
                  value={totals.reserved}
                />
                <KpiTile
                  color={BAR_COLOURS.partsAllocated}
                  label="Op projectonderdelen"
                  value={totals.partsAllocated}
                />
                <KpiTile
                  color={BAR_COLOURS.invoiced}
                  label="Gefactureerd"
                  value={totals.invoiced}
                />
              </div>

              {/* Chart */}
              {visibleBuckets.length === 0 ? (
                <div className="text-neutral-secondary-mid py-6 text-center text-xs italic">
                  Alle labels zijn verborgen — selecteer minstens één label om
                  de grafiek te tonen.
                </div>
              ) : (
                <div className="h-[340px]">
                  <Bar data={chartData} options={chartOptions} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface KpiTileProps {
  color: string;
  label: string;
  value: number;
}

const KpiTile = ({ color, label, value }: KpiTileProps) => (
  <div className="rounded-md border border-neutral-200 bg-white p-2.5">
    <div className="flex items-center gap-1.5">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-neutral-secondary-mid text-[10px] font-semibold uppercase tracking-wide">
        {label}
      </span>
    </div>
    <div className="mt-0.5 text-base font-semibold tabular-nums text-neutral-primary">
      {new Intl.NumberFormat("nl-BE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0)}
    </div>
  </div>
);

export default LabelBudgetStatisticsSection;
